import React from "react";
import { Form, Field } from "react-final-form";
import { FormattedMessage } from "@/i18n/i18n-client";
import createDecorator from "final-form-focus";
import Modal from "@/shared/globals/UiElements/Modal";
import { H2, P, Label, FlexRow, Input } from "@/shared/globals";
import {
  ButtonsContainer,
  SaveButton,
  RequiredSpan,
  CancelButton,
} from "./styled";
import type { FormValues } from "./types";
import InputPhone, { PhoneInput } from "@/shared/globals/UiElements/InputPhone";
import { Delete } from "@/assets/Icons/Delete";

const focusOnError = createDecorator();

interface EditPhoneModalProps {
  isOpen: boolean;
  onClose: () => void;
  phone: PhoneInput;
  onSubmit: (values: FormValues) => void;
  error?: React.ReactNode;
  children?: React.ReactNode;
}

const EditPhoneModal: React.FC<EditPhoneModalProps> = ({
  isOpen,
  onClose,
  phone,
  onSubmit,
  error,
}) => {
  return (
    <Modal
      show={isOpen}
      onClose={onClose}
      modalWidth="small"
      closeIcon={<Delete small />}
    >
      <Modal.Body padding="32px">
        <Form
          onSubmit={onSubmit}
          validate={validate}
          decorators={[focusOnError]}
          initialValues={{ phone }}
          render={({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit}>
              <H2 margin="0px 0px 10px 0px">
                <FormattedMessage
                  defaultMessage="Change your phone"
                  id="CReJpi"
                />
              </H2>
              <P color="secondary">
                <FormattedMessage
                  defaultMessage="If you want to change the phone associated with our store, you may do so below."
                  id="hyeh6z"
                />
              </P>
              <Label>
                <FlexRow>
                  <FormattedMessage defaultMessage="Phone no." id="1SphRN" />
                </FlexRow>
              </Label>
              <Field<PhoneInput> name="phone">
                {(fieldProps) => (
                  <InputPhone {...fieldProps} dataTest="type-primary-phone" />
                )}
              </Field>
              <Label>
                <FormattedMessage
                  defaultMessage="Current password"
                  id="NIynnB"
                />
              </Label>
              <Field name="currentPassword">
                {({ input, meta: { error, touched } }) => (
                  <>
                    <Input
                      {...input}
                      className={error && touched ? "invalid" : ""}
                      data-test="type-password"
                      type="password"
                    />
                    {error && touched && <RequiredSpan>{error}</RequiredSpan>}
                  </>
                )}
              </Field>
              {error && <RequiredSpan>{error}</RequiredSpan>}
              <ButtonsContainer>
                <CancelButton type="button" onClick={onClose}>
                  <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
                </CancelButton>
                <SaveButton
                  data-test="button-save-changes"
                  type="submit"
                  isLoading={submitting}
                >
                  <FormattedMessage defaultMessage="Save Changes" id="3VI9mt" />
                </SaveButton>
              </ButtonsContainer>
            </form>
          )}
        />
      </Modal.Body>
    </Modal>
  );
};

export default EditPhoneModal;

const validate = (values: FormValues) => {
  const errors: Partial<{ [k in keyof FormValues]: React.ReactElement }> = {};
  if (!values?.phone?.isValid) {
    errors.phone = (
      <FormattedMessage
        defaultMessage="Enter a valid phone number"
        id="iydw3u"
      />
    );
  }
  if (!values?.currentPassword) {
    errors.currentPassword = (
      <FormattedMessage defaultMessage="Enter your password" id="2GFjIN" />
    );
  }
  return errors;
};
